import { BodyMedium, BodySmall, theme } from '@arnold/common';
import { ReactComponent as AIIcon } from '@arnold/common/lib/assets/illustrations/AI.svg';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { FC, FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ActionButton } from '../Common';
import {
  ARIEL_AI_FILE_ACCEPT,
  ArielAiChatAttachment,
  MAX_ATTACHMENTS_PER_MESSAGE,
  readFileAsAttachment,
  validateArielAiAttachmentFile,
} from './arielAiAttachments';
import { renderArielAiMarkdown } from './arielAiMarkdown';
import { ArielAiChatMessage } from './useArielAiChat';

const borderColor = theme.colors.actionSecondary.default;
const mutedTextColor = theme.colors.text.secondary;

const ARIEL_AI_SIDEBAR_WIDTH = 360;
const ARIEL_CONTROL_PANEL_HEIGHT = 74;

const Sidebar = styled.aside<{ $open: boolean; $fullscreen: boolean }>`
  display: flex;
  flex-direction: column;
  align-self: stretch;
  height: 100%;
  width: ${({ $open, $fullscreen }) => ($fullscreen ? '0' : $open ? `${ARIEL_AI_SIDEBAR_WIDTH}px` : '0')};
  min-width: ${({ $open, $fullscreen }) => ($fullscreen ? '0' : $open ? `${ARIEL_AI_SIDEBAR_WIDTH}px` : '0')};
  min-height: 0;
  overflow: ${({ $fullscreen, $open }) => ($fullscreen && $open ? 'visible' : 'hidden')};
  border-left: ${({ $open, $fullscreen }) => ($open && !$fullscreen ? `1px solid ${borderColor}` : 'none')};
  background: ${({ $fullscreen }) => ($fullscreen ? 'transparent' : theme.background.primary)};
  transition:
    width 0.2s ease,
    min-width 0.2s ease;
`;

const SidebarInner = styled.div<{ $fullscreen: boolean }>`
  display: flex;
  flex-direction: column;
  width: ${ARIEL_AI_SIDEBAR_WIDTH}px;
  height: calc(100vh - ${ARIEL_CONTROL_PANEL_HEIGHT}px);
  min-height: 0;
  overflow: hidden;
  ${({ $fullscreen }) =>
    $fullscreen &&
    css`
      position: fixed;
      top: ${ARIEL_CONTROL_PANEL_HEIGHT}px;
      right: 0;
      z-index: 1001;
      border-left: 1px solid ${borderColor};
      background: ${theme.background.primary};
    `}
`;

const Header = styled.div`
  display: flex;
  flex-shrink: 0;
  padding: ${theme.spacing.e} ${theme.spacing.f};
  background: ${theme.colors.ai.default};
  color: ${theme.colors.textInverted.primary};
`;

const HeaderContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: ${theme.spacing.b};
  min-width: 0;
`;

const HeaderTitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing.d};
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.d};
  min-width: 0;
`;

const HeaderIcon = styled(AIIcon)`
  flex-shrink: 0;
`;

const HeaderSubtitle = styled(BodySmall)`
  line-height: 1.4;
  opacity: 0.92;
`;

const NewConversationButton = styled.button`
  flex-shrink: 0;
  border: 1px solid rgba(255, 255, 255, 0.45);
  border-radius: 6px;
  background: transparent;
  color: inherit;
  font-family: inherit;
  font-size: 12px;
  padding: ${theme.spacing.b} ${theme.spacing.c};
  cursor: pointer;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.14);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Messages = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: ${theme.spacing.f};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.e};
`;

const MessageBubble = styled.div<{ $role: 'user' | 'assistant' }>`
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  max-width: 90%;
  padding: ${theme.spacing.d} ${theme.spacing.e};
  border-radius: 12px;
  background: ${({ $role }) => ($role === 'user' ? theme.colorTertiary : theme.colors.backgroundBasic.default)};
  white-space: ${({ $role }) => ($role === 'user' ? 'pre-wrap' : 'normal')};
  word-break: break-word;
`;

const AssistantMessageBody = styled.div`
  font-size: 14px;
  line-height: 1.5;

  p {
    margin: 0 0 ${theme.spacing.c};

    &:last-child {
      margin-bottom: 0;
    }
  }

  ul,
  ol {
    margin: 0 0 ${theme.spacing.c};
    padding-left: 1.25em;
  }

  li {
    margin-bottom: ${theme.spacing.b};

    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-weight: 600;
  }

  code {
    font-family: inherit;
    font-size: 0.9em;
    background: ${theme.background.primary};
    padding: 0.1em 0.35em;
    border-radius: 4px;
  }

  h3,
  h4 {
    font-size: 14px;
    font-weight: 600;
    margin: ${theme.spacing.d} 0 ${theme.spacing.c};

    &:first-child {
      margin-top: 0;
    }
  }

  a {
    color: ${theme.colors.brand.primary};
    text-decoration: underline;
  }
`;

const AttachmentList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.c};
  margin-top: ${theme.spacing.c};
`;

const AttachmentChip = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.c};
  font-size: 12px;
  color: ${mutedTextColor};
`;

const AttachmentThumb = styled.img`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid ${borderColor};
`;

const Composer = styled.form`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  gap: ${theme.spacing.d};
  padding: ${theme.spacing.f};
  border-top: 1px solid ${borderColor};
`;

const PendingAttachments = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.c};
`;

const PendingChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.b};
  padding: ${theme.spacing.b} ${theme.spacing.c};
  border: 1px solid ${borderColor};
  border-radius: 999px;
  font-size: 12px;
  max-width: 100%;
`;

const PendingChipName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 180px;
`;

const RemoveChipButton = styled.button`
  border: none;
  background: transparent;
  color: ${mutedTextColor};
  cursor: pointer;
  padding: 0;
  line-height: 1;

  &:hover {
    color: ${theme.colors.text.primary};
  }
`;

const ComposerActions = styled.div`
  display: flex;
  gap: ${theme.spacing.d};
  align-items: center;
  justify-content: space-between;
`;

const ComposerDropZone = styled.div<{ $isDragging: boolean }>`
  position: relative;
  border-radius: 8px;
  outline: ${({ $isDragging }) => ($isDragging ? `2px dashed ${theme.colors.brand.primary}` : 'none')};
  outline-offset: 2px;
  background: ${({ $isDragging }) => ($isDragging ? theme.colorTertiary : 'transparent')};
  transition: background 0.15s ease;
`;

const ComposerTextarea = styled.textarea`
  width: 100%;
  min-height: 72px;
  padding: ${theme.spacing.d};
  border: 1px solid ${borderColor};
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;

  &:disabled {
    opacity: 0.6;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const StreamingIndicator = styled(BodyMedium)`
  color: ${mutedTextColor};
  font-style: italic;
`;

const Shortcuts = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.d};
  margin-top: auto;
`;

const ShortcutsHeading = styled(BodyMedium)`
  font-weight: 600;
  line-height: 1.35;
`;

const ShortcutCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.c};
  width: 100%;
  padding: ${theme.spacing.e};
  border: 1px solid ${borderColor};
  border-radius: 12px;
  background: ${theme.background.primary};
  font-family: inherit;
  text-align: left;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${theme.colorTertiary};
    border-color: ${theme.colors.brand.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ShortcutTitle = styled(BodyMedium)`
  font-weight: 600;
  line-height: 1.35;
`;

const ShortcutDescription = styled(BodySmall)`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  line-height: 1.4;
  color: ${mutedTextColor};
`;

const AttachmentError = styled(BodyMedium)`
  color: ${theme.colors.form.error};
`;

const ArielAiMarkdownContent: FC<{ content: string }> = ({ content }) => (
  <AssistantMessageBody dangerouslySetInnerHTML={{ __html: renderArielAiMarkdown(content) }} />
);

type ArielAiSidebarProps = {
  open: boolean;
  fullscreen?: boolean;
  topicName: string;
  languageCode: string;
  messages: ArielAiChatMessage[];
  streamingText: string;
  isStreaming: boolean;
  isPreparingPatch: boolean;
  isApplyingPatch: boolean;
  onSend: (message: string, attachments?: ArielAiChatAttachment[]) => void;
  onNewConversation?: () => void;
};

export const ArielAiSidebar: FC<ArielAiSidebarProps> = ({
  open,
  fullscreen = false,
  topicName,
  languageCode,
  messages,
  streamingText,
  isStreaming,
  isPreparingPatch,
  isApplyingPatch,
  onSend,
  onNewConversation,
}) => {
  const { t } = useTranslation('topicEditor');
  const emptyStateShortcuts = [
    {
      key: 'formal',
      title: t('arielAiShortcutFormalTitle'),
      description: t('arielAiShortcutFormalDescription'),
      prompt: t('arielAiShortcutFormalPrompt'),
    },
    {
      key: 'risks',
      title: t('arielAiShortcutRisksTitle'),
      description: t('arielAiShortcutRisksDescription'),
      prompt: t('arielAiShortcutRisksPrompt'),
    },
    {
      key: 'consultation',
      title: t('arielAiShortcutConsultationTitle'),
      description: t('arielAiShortcutConsultationDescription'),
      prompt: t('arielAiShortcutConsultationPrompt'),
    },
  ] as const;
  const isBusy = isStreaming || isPreparingPatch || isApplyingPatch;
  const isNewConversation = messages.length === 0 && !streamingText && !isBusy;
  const topicLabel = topicName.trim() || t('arielAiUntitledTopic');
  const languageLabel = languageCode.toUpperCase();
  const [input, setInput] = useState('');
  const [pendingAttachments, setPendingAttachments] = useState<ArielAiChatAttachment[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const messagesRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const dragDepthRef = useRef(0);

  useEffect(() => {
    const messagesEl = messagesRef.current;
    if (!messagesEl) {
      return;
    }
    messagesEl.scrollTo({ top: messagesEl.scrollHeight, behavior: 'smooth' });
  }, [messages, streamingText, isApplyingPatch, isPreparingPatch]);

  const canSend = (input.trim().length > 0 || pendingAttachments.length > 0) && !isBusy;

  const sendMessage = () => {
    if (!canSend) {
      return;
    }
    onSend(input, pendingAttachments);
    setInput('');
    setPendingAttachments([]);
    setAttachmentError(null);
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    sendMessage();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const addFiles = async (files: File[]) => {
    if (files.length === 0 || isBusy) {
      return;
    }

    if (pendingAttachments.length >= MAX_ATTACHMENTS_PER_MESSAGE) {
      setAttachmentError(t('arielAiAttachmentLimit'));
      return;
    }

    const parsedAttachments: ArielAiChatAttachment[] = [];
    const errors: string[] = [];

    for (const file of files) {
      const validationError = validateArielAiAttachmentFile(file);
      if (validationError) {
        errors.push(validationError);
        continue;
      }
      try {
        parsedAttachments.push(await readFileAsAttachment(file));
      } catch (err) {
        errors.push((err as Error).message);
      }
    }

    setPendingAttachments((current) => {
      const remainingSlots = MAX_ATTACHMENTS_PER_MESSAGE - current.length;
      return [...current, ...parsedAttachments.slice(0, Math.min(remainingSlots, parsedAttachments.length))];
    });
    setAttachmentError(errors.length > 0 ? errors.join(' ') : null);
  };

  const handleFilesSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files ?? []);
    event.target.value = '';
    await addFiles(files);
  };

  const handleDragEnter = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (isBusy) {
      return;
    }
    dragDepthRef.current += 1;
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setIsDragging(false);
    }
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleDrop = async (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDragging(false);
    if (isBusy) {
      return;
    }
    const files = Array.from(event.dataTransfer.files ?? []);
    await addFiles(files);
  };

  const removePendingAttachment = (index: number) => {
    setPendingAttachments((current) => current.filter((_, i) => i !== index));
    setAttachmentError(null);
  };

  const handleShortcutClick = (prompt: string) => {
    if (isBusy) {
      return;
    }
    onSend(prompt);
  };

  return (
    <Sidebar $open={open} $fullscreen={fullscreen} aria-hidden={!open}>
      <SidebarInner $fullscreen={fullscreen && open}>
        <Header>
          <HeaderContent>
            <HeaderTitleRow>
              <HeaderTitle>
                <HeaderIcon aria-hidden />
                <BodyMedium color="inherit">{t('arielAiTitle')}</BodyMedium>
              </HeaderTitle>
              {!isNewConversation && onNewConversation && (
                <NewConversationButton
                  type="button"
                  onClick={onNewConversation}
                  disabled={isBusy}
                  data-icom="btn-ariel-ai-new-conversation"
                >
                  {t('arielAiNewConversation')}
                </NewConversationButton>
              )}
            </HeaderTitleRow>
            <HeaderSubtitle color="inherit">
              {isNewConversation
                ? t('arielAiHeaderNewConversation', {
                    topicName: topicLabel,
                    language: languageLabel,
                  })
                : t('arielAiHeaderContext', {
                    topicName: topicLabel,
                    language: languageLabel,
                  })}
            </HeaderSubtitle>
          </HeaderContent>
        </Header>
        <Messages ref={messagesRef}>
          {isNewConversation && (
            <Shortcuts>
              <ShortcutsHeading>{t('arielAiShortcutsHeading')}</ShortcutsHeading>
              {emptyStateShortcuts.map(({ key, title, description, prompt }) => (
                <ShortcutCard
                  key={key}
                  type="button"
                  onClick={() => handleShortcutClick(prompt)}
                  disabled={isBusy}
                  data-icom={`btn-ariel-ai-shortcut-${key}`}
                >
                  <ShortcutTitle>{title}</ShortcutTitle>
                  <ShortcutDescription>{description}</ShortcutDescription>
                </ShortcutCard>
              ))}
            </Shortcuts>
          )}
          {messages.map((message, index) => (
            <MessageBubble key={`${message.role}-${index}`} $role={message.role}>
              {message.role === 'assistant' ? <ArielAiMarkdownContent content={message.content} /> : message.content}
              {message.attachments && message.attachments.length > 0 && (
                <AttachmentList>
                  {message.attachments.map((attachment) => (
                    <AttachmentChip key={`${attachment.filename}-${index}`}>
                      {attachment.previewDataUrl ? (
                        <AttachmentThumb src={attachment.previewDataUrl} alt={attachment.filename} />
                      ) : null}
                      <span>{attachment.filename}</span>
                    </AttachmentChip>
                  ))}
                </AttachmentList>
              )}
            </MessageBubble>
          ))}
          {streamingText && (
            <MessageBubble $role="assistant">
              <ArielAiMarkdownContent content={streamingText} />
            </MessageBubble>
          )}
          {isStreaming && !isApplyingPatch && !isPreparingPatch && !streamingText && (
            <StreamingIndicator>{t('arielAiThinking')}</StreamingIndicator>
          )}
          {isPreparingPatch && !isApplyingPatch && (
            <StreamingIndicator>{t('arielAiPreparingPatch')}</StreamingIndicator>
          )}
          {isApplyingPatch && <StreamingIndicator>{t('arielAiApplyingPatch')}</StreamingIndicator>}
        </Messages>
        <Composer onSubmit={handleSubmit}>
          {pendingAttachments.length > 0 && (
            <PendingAttachments>
              {pendingAttachments.map((attachment, index) => (
                <PendingChip key={`${attachment.filename}-${index}`}>
                  <PendingChipName title={attachment.filename}>{attachment.filename}</PendingChipName>
                  <RemoveChipButton
                    type="button"
                    onClick={() => removePendingAttachment(index)}
                    aria-label={t('arielAiRemoveAttachment')}
                  >
                    ×
                  </RemoveChipButton>
                </PendingChip>
              ))}
            </PendingAttachments>
          )}
          {attachmentError && <AttachmentError>{attachmentError}</AttachmentError>}
          <ComposerDropZone
            $isDragging={isDragging}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            <ComposerTextarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('arielAiInputPlaceholder')}
              rows={3}
              disabled={isBusy}
            />
          </ComposerDropZone>
          <ComposerActions>
            <ActionButton type="submit" variant="primary" size="sm" disabled={!canSend} data-icom="btn-send-ariel-ai">
              {t('arielAiSend')}
            </ActionButton>
            <ActionButton
              type="button"
              variant="outline-primary"
              size="sm"
              onClick={handleAttachClick}
              disabled={isBusy || pendingAttachments.length >= MAX_ATTACHMENTS_PER_MESSAGE}
              data-icom="btn-attach-ariel-ai"
            >
              {t('arielAiAttach')}
            </ActionButton>
          </ComposerActions>
          <HiddenFileInput
            ref={fileInputRef}
            type="file"
            multiple
            accept={ARIEL_AI_FILE_ACCEPT}
            onChange={handleFilesSelected}
          />
        </Composer>
      </SidebarInner>
    </Sidebar>
  );
};
